/**
 * Browser compatibility utilities for MediaRecorder API
 * Handles detection and configuration for different browsers and devices
 */

import type { AudioConstraints, AudioFormatConfig, BrowserCompatibility } from '../models/audio-recorder.types';
import { PermissionState } from '../models/audio-recorder.types';

/**
 * Check if MediaRecorder API is supported
 */
export const isMediaRecorderSupported = (): boolean => {
  return !!(typeof navigator.mediaDevices?.getUserMedia === 'function' && window.MediaRecorder);
};

/**
 * Check if getUserMedia is supported
 */
export const isGetUserMediaSupported = (): boolean => {
  return !!navigator.mediaDevices?.getUserMedia;
};

/**
 * Detect if current browser is Safari
 */
export const isSafari = (): boolean => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

/**
 * Detect if current device is iOS
 */
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Detect if current device is mobile
 */
export const isMobile = (): boolean => {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Check if specific MIME type is supported
 */
export const isMimeTypeSupported = (mimeType: string): boolean => {
  if (!isMediaRecorderSupported()) return false;

  try {
    return MediaRecorder.isTypeSupported(mimeType);
  } catch (error) {
    console.warn(`Error checking MIME type support for ${mimeType}:`, error);
    return false;
  }
};

/**
 * Get all supported MIME types
 */
export const getSupportedMimeTypes = (): string[] => {
  if (!isMediaRecorderSupported()) return [];

  const types = [
    // Opus in WebM - best for Chrome/Firefox/Edge
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    // AAC in MP4 - required for Safari/iOS
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/mpeg'
  ];

  return types.filter(isMimeTypeSupported);
};

/**
 * Get optimal audio format configuration based on browser capabilities
 */
export const getOptimalAudioConfig = (): AudioFormatConfig | null => {
  const mobile = isMobile();

  // Priority 1: AAC/MP4 (best compatibility and quality)
  if (isMimeTypeSupported('audio/mp4;codecs=mp4a.40.2')) {
    return {
      mimeType: 'audio/mp4;codecs=mp4a.40.2',
      codec: 'aac',
      estimatedBitrate: mobile ? 64000 : 128000,
      fileExtension: 'aac',
      quality: 'standard'
    };
  }

  // Priority 2: Basic MP4 fallback
  if (isMimeTypeSupported('audio/mp4')) {
    return {
      mimeType: 'audio/mp4',
      codec: 'mp4',
      estimatedBitrate: mobile ? 64000 : 128000,
      fileExtension: 'aac',
      quality: 'standard'
    };
  }

  // Priority 3: Opus/WebM for browsers that don't support AAC
  if (isMimeTypeSupported('audio/webm;codecs=opus')) {
    return {
      mimeType: 'audio/webm;codecs=opus',
      codec: 'opus',
      audioBitsPerSecond: mobile ? 32000 : 64000,
      fileExtension: 'webm',
      quality: 'optimized'
    };
  }

  // Priority 4: Basic WebM
  if (isMimeTypeSupported('audio/webm')) {
    return {
      mimeType: 'audio/webm',
      codec: 'webm',
      audioBitsPerSecond: mobile ? 48000 : 64000,
      fileExtension: 'webm',
      quality: 'standard'
    };
  }

  // Priority 5: Ogg/Opus fallback
  if (isMimeTypeSupported('audio/ogg;codecs=opus')) {
    return {
      mimeType: 'audio/ogg;codecs=opus',
      codec: 'opus',
      audioBitsPerSecond: 32000,
      fileExtension: 'ogg',
      quality: 'optimized'
    };
  }

  // No supported format found
  return null;
};

/**
 * Get optimal audio constraints for getUserMedia
 */
export const getAudioConstraints = (): AudioConstraints => {
  const mobile = isMobile();
  const ios = isIOS();

  if (mobile) {
    return {
      audio: {
        channelCount: 1, // Mono for voice
        sampleRate: ios ? 16000 : 16000, // 16kHz for mobile
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    };
  }

  // Desktop configuration
  return {
    audio: {
      channelCount: 1, // Mono for voice
      sampleRate: 24000, // 24kHz for desktop
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    }
  };
};

/**
 * Get comprehensive browser compatibility information
 */
export const getBrowserCompatibility = (): BrowserCompatibility => {
  const mediaRecorderSupported = isMediaRecorderSupported();
  const getUserMediaSupported = isGetUserMediaSupported();
  const supportedMimeTypes = getSupportedMimeTypes();
  const recommendedFormat = getOptimalAudioConfig();

  return {
    isMediaRecorderSupported: mediaRecorderSupported,
    isGetUserMediaSupported: getUserMediaSupported,
    supportedMimeTypes,
    recommendedFormat,
    isMobile: isMobile(),
    isIOS: isIOS(),
    isSafari: isSafari()
  };
};

/**
 * Check microphone permission status
 */
export const checkMicrophonePermission = async (): Promise<PermissionState> => {
  try {
    // Try navigator.permissions API (not supported in Safari)
    if (navigator.permissions?.query) {
      const permissionStatus = await navigator.permissions.query({
        name: 'microphone' as PermissionName
      });
      return permissionStatus.state as PermissionState;
    }
  } catch (error) {
    // Permissions API not supported or failed
    console.warn('Permissions API not available:', error);
  }

  // Fallback: try to get user media to check permission
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return PermissionState.GRANTED;
  } catch (error) {
    if ((error as Error).name === 'NotAllowedError') {
      return PermissionState.DENIED;
    }
    return PermissionState.UNKNOWN;
  }
};

/**
 * Get Android version from user agent
 */
export const getAndroidVersion = (): number | null => {
  const match = navigator.userAgent.match(/Android (\d+)/);
  return match ? Number.parseInt(match[1], 10) : null;
};

/**
 * Get iOS version from user agent
 */
export const getIOSVersion = (): number | null => {
  const match = navigator.userAgent.match(/OS (\d+)_/);
  return match ? Number.parseInt(match[1], 10) : null;
};

/**
 * Check if browser supports audio worklets (for advanced processing)
 */
export const supportsAudioWorklet = (): boolean => {
  return (
    !!(window.AudioContext || (window as any).webkitAudioContext) &&
    'audioWorklet' in (window.AudioContext?.prototype || {})
  );
};
